import React, { useCallback, useEffect, useMemo, useState } from "react"
import type { WidgetProps } from "@rjsf/core"
import { MultipleChoice, ExclusiveChoiceDropdown } from "dash-styled"
import type { JSONSchema7Type } from "json-schema"
import { TextInput } from "resha"

interface EnumOption {
    label: string
    value: string
}

function renderEnumOption(option: EnumOption) {
    return option.label
}

function MultipleSelectWidget({
    readOnly,
    value,
    choices,
    onChange
}: {
    readOnly: boolean
    choices: EnumOption[]
    value: unknown
    onChange: (value: unknown) => void
}) {
    const labels = useMemo(() => choices.map(renderEnumOption), [choices])
    const selectedIndices = useMemo<number[]>(
        () =>
            Array.isArray(value)
                ? value.flatMap(item => {
                      const index = choices.findIndex(c => c.value === item)

                      return item === -1 ? [] : [index]
                  })
                : [],
        [choices, value]
    )
    const handleChange = useCallback(
        (index: number) => {
            const item = choices[index]

            if (item === undefined) {
                return
            }

            if (Array.isArray(value)) {
                onChange(
                    value.includes(item.value)
                        ? value.filter(i => i !== item.value)
                        : value.concat([item.value])
                )
            } else {
                onChange([])
            }
        },
        [choices, onChange, value]
    )

    return (
        <MultipleChoice
            choices={labels}
            selectedIndices={selectedIndices}
            variant="chip"
            interactive={!readOnly}
            hideUnchecked={readOnly}
            inlineItems={true}
            withPanel={false}
            onClick={handleChange}
        />
    )
}

function SingleSelectWidget({
    readOnly,
    value,
    choices,
    choiceColors,
    defaultValue,
    onChange
}: {
    readOnly: boolean
    choices: EnumOption[]
    value: unknown
    choiceColors?: string[]
    defaultValue: JSONSchema7Type | undefined
    onChange: (value: unknown) => void
}) {
    const [searchText, setSearchText] = useState("")
    const filteredChoices = useMemo(
        () =>
            choices.filter(({ label }) =>
                label.toLowerCase().includes(searchText.toLowerCase())
            ),
        [choices, searchText]
    )
    const labels = useMemo(
        () => filteredChoices.map(renderEnumOption),
        [filteredChoices]
    )

    const handleEnter = (selected: string, highlightedIndex: number) => {
        let item
        if (highlightedIndex >= 0) {
            item = filteredChoices[highlightedIndex]
        } else if (selected) {
            item = filteredChoices.find(c =>
                c.label.toLowerCase().includes(selected.toLowerCase())
            )
        }
        if (item?.value !== undefined) {
            onChange(item?.value)
            setSearchText("")
        }
    }

    const handleChange = useCallback(
        (index: number) => {
            const item = filteredChoices[index]

            if (item === undefined) {
                return
            }

            onChange(item.value)
        },
        [filteredChoices, onChange]
    )

    const renderExclusiveChoice = useCallback(
        label => {
            if (!choiceColors) {
                return label
            }

            const idx = choices.findIndex(choice => choice.label === label)
            return (
                <div
                    className="flex flex-row items-center gap-1"
                    key={choices[idx]?.value}
                >
                    <div
                        className="w-4 h-4 rounded-sm"
                        style={{ backgroundColor: choiceColors[idx] }}
                    ></div>
                    <div>{label}</div>
                </div>
            )
        },
        [choiceColors, choices]
    )

    useEffect(() => {
        if (value === undefined && defaultValue !== undefined) {
            // we want explicitly modify value to default to make that it will be saved
            onChange(defaultValue)
        }
    }, [value, defaultValue, onChange])

    if (readOnly) {
        const label = choices.find(c => c.value === value)?.label
        return label ? (
            <TextInput
                singleLine
                readOnly
                enableMarkdown={false}
                enableCopyTextButton={false}
                value={label}
                inputClassName="rounded-lg bg-transparent text-sm leading-5"
                className="bg-rhino-300 opacity-60 py-2 px-3 rounded-lg border border-rhino-300"
            />
        ) : null
    }

    return (
        <ExclusiveChoiceDropdown
            choices={labels}
            renderChoice={renderExclusiveChoice}
            selectedIndex={choices.findIndex(c => c.value === value)}
            onClick={handleChange}
            onSearch={setSearchText}
            onEnter={handleEnter}
            className="space-y-1"
            inputClassName="text-sm leading-6 py-2.5 border-rhino-300"
            testId="exclusive-choice-container"
        />
    )
}

export function SelectWidget({
    schema,
    value,
    disabled,
    readonly,
    onChange,
    options,
    multiple
}: WidgetProps) {
    if (!options.enumOptions) {
        return null
    }

    if (multiple) {
        return (
            <MultipleSelectWidget
                value={value}
                choices={options.enumOptions as EnumOption[]}
                readOnly={readonly || disabled}
                onChange={onChange}
            />
        )
    }

    return (
        <SingleSelectWidget
            value={value}
            choices={options.enumOptions as EnumOption[]}
            choiceColors={options.enumColors as string[]}
            readOnly={readonly || disabled}
            onChange={onChange}
            defaultValue={schema.default}
        />
    )
}
